import { generateKey } from '@kineticdata/react';
import { Record, List, OrderedMap, Map } from 'immutable';
import { getRequiredDefaultContentArray } from './defaults';

export const CONTENT_MANAGER_MODE = {
  PUBLIC: 0,
  MANAGE: 1,
  EDIT: 2,
  SUBMIT: 3,
  REVIEW: 4,
};
export const SHORT_DESCRIPTION_MAX_LENGTH = 512;

export const getStatusColor = status => {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Draft':
      return 'info';
    case 'Pending':
      return 'warning';
    default:
      return 'dark';
  }
};

export const getValidSectionId = (
  content,
  mode,
  currentId,
  initialSectionName,
) => {
  return (
    (initialSectionName &&
      !currentId &&
      content.find(item => item.title === initialSectionName)) ||
    (currentId && content.find(item => item.id === currentId)) ||
    (mode === CONTENT_MANAGER_MODE.PUBLIC
      ? content.find(item => !item.settings)
      : content.first()) ||
    {}
  ).id;
};

export const Content = Record({
  id: generateKey(8),
  parentId: null,
  title: '',
  value: '',
  type: '', // Public, Internal, Hidden
  updatedAt: new Date().toISOString(),
  settings: false,
  rendererId: null,
  // Transient value only used for the UI and not stored
  isDirty: false,
  ancestorIds: List(),
});

const buildAncestorList = (id, items, list = List()) => {
  const item = items.find(i => i.id === id);
  if (item) {
    if (item.parentId) {
      return buildAncestorList(item.parentId, items, list.push(id));
    } else {
      return list.push(id);
    }
  } else {
    return list;
  }
};

// Creates a string with ordered section ids, parentIds, and setting flag data
// for comparing whether the section order has changed
export const getContentOrderString = content =>
  content
    ? content
        .toList()
        .map(
          ({ id, parentId, settings }) =>
            `${id},${parentId || ''},${!!settings ? 's' : ''}`,
        )
        .join(';')
    : '';

// Converts a contentOrderString into a map of index values for each of the
// content sections.
export const getContentIndexMap = (
  contentOrderString,
  previousId,
  previousIndex,
  resultMap = Map(),
) => {
  if (!contentOrderString) {
    return null;
  }
  // Parse contentOrderString and filter to only non setting records at the
  // correct level of the hierarchy.
  const data = contentOrderString
    .split(';')
    .map(el => el.split(','))
    .filter(
      ([id, parentId, settings]) =>
        !settings && ((!previousId && !parentId) || previousId === parentId),
    );

  if (!data || data.length === 0) {
    return resultMap;
  } else {
    return data.reduce((map, [id], idx) => {
      const index = [previousIndex, idx + 1].filter(Boolean).join('.');
      return getContentIndexMap(
        contentOrderString,
        id,
        index,
        map.set(id, index),
      );
    }, resultMap);
  }
};

export const buildContentFromSubmission = submission => {
  const contentJSONString =
    (submission && submission.values && submission.values.Content) || '[]';
  try {
    const contentJSON = JSON.parse(contentJSONString);
    if (Array.isArray(contentJSON)) {
      // Build an OrderedMap of all Content elements using the id as the key.
      let content = contentJSON
        .reduce((map, item) => map.set(item.id, Content(item)), OrderedMap())
        // If there are any orphaned items, remove their parent id so they show at the top level
        .map(
          (item, id, arr) =>
            item.parentId && !arr.find(a => a.id === item.parentId)
              ? item.set('parentId', null)
              : item,
        )
        // Populate ancestorIds value for the map
        .map(
          (item, id, arr) =>
            item.parentId
              ? item.set('ancestorIds', buildAncestorList(item.parentId, arr))
              : item,
        );
      // Get missing default content sections, which are any settings sections
      // which don't exist in the current content.
      const missingDefaultContent = getRequiredDefaultContentArray(
        submission.form && submission.form.slug,
      ).filter(dc => !content.find(c => c.rendererId === dc.rendererId));
      // Return the content with any missing sections from defaultContent added
      return missingDefaultContent.reduce(
        (map, item) => map.set(item.id, Content(item)),
        content,
      );
    } else {
      // Return null if Content value isn't an array. This means the submission
      // value is corrupted.
      return null;
    }
  } catch (e) {
    // Return null if Content value isn't valid JSON. This means the submission
    // value is corrupted.
    return null;
  }
};

export const buildSubmissionValueFromContent = content => {
  return JSON.stringify(
    content
      .map(item => item.delete('isDirty').delete('ancestorIds'))
      .toList()
      .toJS(),
  );
};
